import { action, thunk, computed } from 'easy-peasy';
import { csvParse } from 'd3-dsv';

import { clamp } from '~/utils';

const dataSelector = (state) => state.data;
const currDataSelector = (state) =>
  state.data.find((d) => d.year === state.year);
const weightedIncomeSelector = (state, storeState) =>
  storeState.income.weightedIncome;
const currPercentageSelector = (state) =>
  state.percentile?.[config.columns.PERCENTAGE];
const zoomViewSelector = (state) => state.isZoomView;
const relViewSelector = (state) => state.isRelView;
const yExtentSelector = (state) => state.yExtent;
const yExtentRelSelector = (state) => state.yExtentRel;

// when we zoom in we add/subtract zoomYRange
const zoomYRange = 10;

export default {
  isRelView: true,
  isZoomView: false,
  year: 2019,
  yExtent: [],
  yExtentRel: [],

  numberFormat: computed((state) => (state.isRelView ? '.1f' : '$,')),
  unit: computed((state) => (state.isRelView ? ' Prozent' : ' Euro')),

  setYear: action((state, year) => {
    state.year = year;
  }),

  changeYear: thunk((actions, year, helpers) => {
    const { data, percentile } = helpers.getState();
    const { income: incomeState } = helpers.getStoreState();
    const { income: incomeActions } = helpers.getStoreActions();

    const currData = data
      .find((d) => d.year === year)
      .data.find(
        (d) =>
          d[config.columns.PERCENTAGE] === percentile[config.columns.PERCENTAGE]
      );

    const upperLimit =
      currData[config.columns.AVG] *
      (1 +
        (incomeState.membersAbove14 - 1) * 0.5 +
        incomeState.membersBelow14 * 0.3);

    incomeActions.setIncome(Math.ceil(upperLimit));
    actions.setYear(year);
  }),

  setRelView: action((state, isRelView) => {
    state.isRelView = isRelView;
  }),

  setZoomView: action((state, isZoomView) => {
    state.isZoomView = isZoomView;
  }),

  isLoading: computed((state) => !state.data),
  setLoading: action((state, isLoading) => {
    state.isLoading = isLoading;
  }),

  data: [],

  loadDataSuccess: action((state, jsonData) => {
    state.data = jsonData.data.map((d) => ({
      ...d,
      data: csvParse(d.data, (row) =>
        Object.keys(row).reduce((res, item) => {
          res[item] = +row[item];
          return res;
        }, {})
      ),
    }));

    state.yExtent = jsonData.yExtent;
    state.yExtentRel = jsonData.yExtentRel;
  }),

  loadData: thunk(async (actions) => {
    try {
      const response = await fetch('public/data/data.json');
      const data = await response.json();
      actions.loadDataSuccess(data);
    } catch (err) {
      console.log('error', err);
    }
  }),

  percentile: computed(
    [currDataSelector, weightedIncomeSelector],
    (currData, weightedIncome) =>
      currData?.data?.find((d) => weightedIncome <= d[config.columns.AVG])
  ),

  ySelector: computed((state) =>
    state.isRelView ? config.columns.TAXES_REL : config.columns.TAXES_EUR
  ),

  yDomain: computed(
    [
      dataSelector,
      currPercentageSelector,
      zoomViewSelector,
      relViewSelector,
      yExtentSelector,
      yExtentRelSelector,
    ],
    (data, percentage, isZoomView, isRelView, yExtent, yExtentRel) => {
      if (isZoomView) {
        const minPerc = clamp(percentage - zoomYRange, 1, 100);
        const maxPerc = clamp(percentage + zoomYRange, 1, 100);

        const yValues = data.reduce((res, item) => {
          const values = item.data
            .filter(
              (d) =>
                d[config.columns.PERCENTAGE] >= minPerc &&
                d[config.columns.PERCENTAGE] <= maxPerc
            )
            .map(
              (d) =>
                d[
                  isRelView
                    ? config.columns.TAXES_REL
                    : config.columns.TAXES_EUR
                ]
            );

          return res.concat(...values);
        }, []);

        const maxValue = Math.max(...yValues);
        const minValue = Math.min(...yValues);
        const yMax = isRelView
          ? Math.ceil(maxValue)
          : Math.ceil(maxValue * 100) / 100;
        const yMin = isRelView
          ? Math.floor(minValue)
          : Math.floor(minValue * 100) / 100;

        return [yMin, yMax];
      }

      return isRelView ? yExtentRel : yExtent;
    }
  ),

  xDomain: computed(
    [currPercentageSelector, zoomViewSelector],
    (percentage, isZoomView) => {
      if (isZoomView) {
        return [
          clamp(percentage - zoomYRange, 1, 100),
          clamp(percentage + zoomYRange, 1, 100),
        ];
      }

      return [1, 100];
    }
  ),
};
